import { Button, EmptyState } from '@ohear/ui'
/**
 * Top-level React ErrorBoundary.
 *
 * Catches render-phase exceptions (which `window.onerror` cannot see) and
 * reports them to /api/v1/telemetry/client-error via the same reporter the
 * global handlers use. The boundary's job is two-fold:
 *
 *   1. Send a structured report INCLUDING the React component stack —
 *      `componentStack` is the part you actually need to find the bug.
 *   2. Render a friendly fallback so the whole app doesn't disappear
 *      behind a blank white page when one route throws.
 *
 * Wraps the RouterProvider in main.tsx; per-route boundaries can override
 * by mounting their own boundary further down the tree.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { reportClientError } from '../lib/error-reporter'

interface ErrorBoundaryProps {
  children: ReactNode
  /** Optional override for the fallback UI. */
  fallback?: (state: { error: Error; reset: () => void }) => ReactNode
}

interface ErrorBoundaryState {
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // We deliberately bypass the dedupe here — every render-phase crash is
    // worth a report. (The reporter still throttles by stack-fingerprint, so
    // a runaway render-loop won't flood the server.)
    const stackParts: string[] = []
    if (error.stack) stackParts.push(error.stack)
    if (info.componentStack) {
      stackParts.push('--- React component stack ---')
      stackParts.push(info.componentStack)
    }
    reportClientError({
      message: error.message || 'React render error',
      stack: stackParts.join('\n'),
      source: 'errorBoundary',
    })
  }

  reset = (): void => {
    this.setState({ error: null })
  }

  override render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    if (this.props.fallback) {
      return this.props.fallback({ error, reset: this.reset })
    }

    return (
      <div className="p-6">
        <EmptyState
          title="Something went wrong on this page"
          description={
            <>
              We've logged the problem and our team will take a look. You can try the page again, or
              head back to the dashboard.
              <br />
              <span className="block mt-2 font-mono text-[12px] text-[var(--text-3)]">
                {error.message}
              </span>
            </>
          }
          action={
            <div className="flex gap-2">
              <Button onClick={this.reset}>Try again</Button>
              <Button
                variant="ghost"
                onClick={() => {
                  if (typeof location !== 'undefined') location.assign('/')
                }}
              >
                Go to dashboard
              </Button>
            </div>
          }
        />
      </div>
    )
  }
}
